import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, MapPin } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { locationsApi, type WorldLocation } from '../api'

interface WorldLocationSearchProps {
    value?: number | null
    onValueChange: (value: number | null) => void
    onLocationSelect?: (location: WorldLocation | null) => void
    disabled?: boolean
}

export function WorldLocationSearch({ value, onValueChange, onLocationSelect, disabled }: WorldLocationSearchProps) {
    const { t } = useTranslation()
    const [search, setSearch] = useState('')
    const [worldLocations, setWorldLocations] = useState<WorldLocation[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [showDropdown, setShowDropdown] = useState(false)
    const [selectedLocation, setSelectedLocation] = useState<WorldLocation | null>(null)
    const [isInitialLoad, setIsInitialLoad] = useState(true)
    const containerRef = useRef<HTMLDivElement>(null)

    // Fetch world locations when search changes
    useEffect(() => {
        const fetchLocations = async () => {
            if (search.length === 0) {
                setWorldLocations([])
                setShowDropdown(false)
                return
            }

            setIsLoading(true)
            setShowDropdown(true)
            try {
                const response = await locationsApi.searchWorldLocations(search, 50)
                setWorldLocations(response.data.data || [])
            } catch (error) {
                console.error('Error fetching world locations:', error)
                setWorldLocations([])
            } finally {
                setIsLoading(false)
            }
        }

        const timeoutId = setTimeout(fetchLocations, 300) // Debounce
        return () => clearTimeout(timeoutId)
    }, [search])

    // Fetch the selected location by ID when value is provided (initial load or value change)
    useEffect(() => {
        const fetchLocationById = async () => {
            if (!value) {
                // No value, clear selection
                if (selectedLocation) {
                    setSelectedLocation(null)
                }
                if (!showDropdown && search) {
                    setSearch('')
                }
                setIsInitialLoad(false)
                return
            }

            // Check if we already have the correct location selected
            if (selectedLocation?.id === value) {
                // Already have the correct location, just ensure search is set
                if (search !== selectedLocation.displayName) {
                    setSearch(selectedLocation.displayName)
                }
                setIsInitialLoad(false)
                return
            }

            // Try to find in current results first
            const found = worldLocations.find(loc => loc.id === value)
            if (found) {
                setSelectedLocation(found)
                setSearch(found.displayName)
                setIsInitialLoad(false)
                return
            }

            // If not found, fetch it directly by ID
            try {
                setIsLoading(true)
                const response = await locationsApi.getWorldLocationById(value)
                const backendResponse = response.data
                const locations = Array.isArray(backendResponse?.data) ? backendResponse.data : []
                const location = locations.length > 0 ? locations[0] : null
                if (location && location.id === value) {
                    setSelectedLocation(location)
                    setSearch(location.displayName)
                    onLocationSelect?.(location)
                } else {
                    // Location not found - log warning but don't clear the value
                    console.warn(`World location with ID ${value} not found`)
                }
            } catch (error) {
                console.error('Error fetching world location by ID:', error)
            } finally {
                setIsLoading(false)
                setIsInitialLoad(false)
            }
        }

        // Always fetch on initial load or when value changes
        fetchLocationById()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]) // Only depend on value to avoid infinite loops

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setShowDropdown(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleSelect = (location: WorldLocation) => {
        setSelectedLocation(location)
        setSearch(location.displayName)
        onValueChange(location.id)
        onLocationSelect?.(location)
        setShowDropdown(false)
    }

    const handleClear = () => {
        setSearch('')
        setSelectedLocation(null)
        onValueChange(null)
        onLocationSelect?.(null)
        setShowDropdown(false)
    }

    return (
        <div ref={containerRef} className="relative w-full">
            <div className="relative">
                <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                    type="text"
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value)
                        if (e.target.value === '') {
                            handleClear()
                        }
                    }}
                    onFocus={() => {
                        if (search.length > 0) {
                            setShowDropdown(true)
                        }
                    }}
                    placeholder={t('locations.searchWorldLocations')}
                    disabled={disabled}
                    className="pl-9"
                />
                {isLoading && (
                    <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                )}
            </div>
            {showDropdown && (worldLocations.length > 0 || isLoading) && (
                <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
                    <div className="max-h-60 overflow-auto p-1">
                        {isLoading ? (
                            <div className="flex items-center justify-center p-4">
                                <Loader2 className="h-4 w-4 animate-spin" />
                            </div>
                        ) : worldLocations.length === 0 ? (
                            <div className="px-2 py-1.5 text-sm text-muted-foreground">
                                {t('locations.noWorldLocationsFound')}
                            </div>
                        ) : (
                            worldLocations.map((location) => (
                                <div
                                    key={location.id}
                                    onClick={() => handleSelect(location)}
                                    className={cn(
                                        'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                                        value === location.id && 'bg-accent'
                                    )}
                                >
                                    {location.displayName}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

